import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';

const inputClass = 'block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6';
const btnPrimary = 'inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toISOString().slice(0, 10);
  } catch {
    return String(iso);
  }
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(11, 19);
  } catch {
    return '';
  }
}

export default function CashTransactionsReport({ authAxios, showToast }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [requireReason, setRequireReason] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);

  const params = useMemo(() => {
    const p = {};
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    p.require_reason = requireReason ? 'true' : 'false';
    return p;
  }, [startDate, endDate, requireReason]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await authAxios.get('invoice-journals/cash-transactions-report/', { params });
      const data = resp.data;
      const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      setRows(results);
      if (typeof showToast === 'function') {
        showToast(`Loaded ${results.length} cash transaction(s).`, 'success');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to load cash transactions report.';
      setError(msg);
      if (typeof showToast === 'function') showToast(msg, 'danger');
    } finally {
      setLoading(false);
    }
  }, [authAxios, params, showToast]);

  useEffect(() => {
    // Load immediately when the tab is opened.
    fetchReport();
  }, [fetchReport]);

  const exportPdf = useCallback(() => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const marginX = 40;
    const marginY = 40;
    let y = marginY;

    const title = 'Cash Transactions Report (P10,000+)';
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(title, marginX, y);

    y += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toISOString().slice(0, 19).replace('T', ' ')}`, marginX, y);

    y += 14;
    const filterText = `Filters: start=${startDate || '—'} | end=${endDate || '—'} | require reason=${requireReason ? 'yes' : 'no'}`;
    doc.text(filterText, marginX, y);

    y += 18;

    // Table layout
    const cols = [
      { header: 'Date', key: 'date', width: 70 },
      { header: 'Time', key: 'time', width: 55 },
      { header: 'Invoice', key: 'invoice', width: 90 },
      { header: 'Client', key: 'client', width: 160 },
      { header: 'Amount (BWP)', key: 'amount', width: 90 },
      { header: 'Recorded By', key: 'by', width: 90 },
      { header: 'Reason', key: 'reason', width: 220 },
    ];

    const tableX = marginX;
    const tableWidth = cols.reduce((acc, c) => acc + c.width, 0);

    // If table is wider than page, scale down widths proportionally
    const maxTableWidth = pageWidth - marginX * 2;
    const scale = tableWidth > maxTableWidth ? (maxTableWidth / tableWidth) : 1;
    const scaledCols = cols.map(c => ({ ...c, width: Math.floor(c.width * scale) }));

    const drawHeader = () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      let x = tableX;
      scaledCols.forEach(c => {
        doc.text(String(c.header), x + 2, y);
        x += c.width;
      });
      doc.setFont('helvetica', 'normal');
      y += 10;
      doc.setDrawColor(180);
      doc.line(tableX, y, tableX + scaledCols.reduce((acc, c) => acc + c.width, 0), y);
      y += 10;
    };

    const newPageIfNeeded = (neededHeight) => {
      if (y + neededHeight > pageHeight - marginY) {
        doc.addPage();
        y = marginY;
        drawHeader();
      }
    };

    drawHeader();

    doc.setFontSize(9);

    const safe = (v) => (v == null ? '' : String(v));

    if (!rows.length) {
      doc.text('No results for the selected filters.', tableX, y);
    } else {
      for (const r of rows) {
        const row = {
          date: formatDate(r.created_at),
          time: formatTime(r.created_at),
          invoice: safe(r.invoice_number),
          client: safe(r.client_name),
          amount: safe(r.amount),
          by: safe(r.recorded_by),
          reason: safe(r.reason),
        };

        // Measure row height by wrapping long text
        const wrappedReason = doc.splitTextToSize(row.reason, scaledCols.find(c => c.key === 'reason')?.width - 6 || 200);
        const wrappedClient = doc.splitTextToSize(row.client, scaledCols.find(c => c.key === 'client')?.width - 6 || 150);
        const lines = Math.max(
          wrappedReason.length,
          wrappedClient.length,
          1
        );
        const rowHeight = 12 * lines;

        newPageIfNeeded(rowHeight + 8);

        let x = tableX;
        const cells = [
          { key: 'date', text: [row.date] },
          { key: 'time', text: [row.time] },
          { key: 'invoice', text: [row.invoice] },
          { key: 'client', text: wrappedClient },
          { key: 'amount', text: [row.amount] },
          { key: 'by', text: [row.by] },
          { key: 'reason', text: wrappedReason },
        ];

        // draw each cell
        for (let i = 0; i < scaledCols.length; i++) {
          const col = scaledCols[i];
          const cell = cells[i];
          const cellText = cell?.text || [''];
          doc.text(cellText, x + 2, y);
          x += col.width;
        }

        y += rowHeight;
        doc.setDrawColor(235);
        doc.line(tableX, y, tableX + scaledCols.reduce((acc, c) => acc + c.width, 0), y);
        y += 8;
      }
    }

    const filename = `cash_transactions_report_${(startDate || 'all')}_${(endDate || 'all')}.pdf`;
    doc.save(filename);
  }, [rows, startDate, endDate, requireReason]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">Cash Transactions Report</h3>
          <p className="text-sm text-gray-500">Flags cash payments P10,000 and above (trust deposits excluded).</p>
        </div>

        <div className="flex items-center gap-2">
          <button className={btnSecondary} onClick={fetchReport} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button className={btnPrimary} onClick={exportPdf} disabled={loading || rows.length === 0}>
            Export PDF
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-900">Start date</label>
          <input className={`${inputClass} mt-1`} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900">End date</label>
          <input className={`${inputClass} mt-1`} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div className="md:col-span-2 flex items-end">
          <label className="inline-flex items-center gap-2 text-sm text-gray-900">
            <input type="checkbox" checked={requireReason} onChange={(e) => setRequireReason(e.target.checked)} />
            Only include rows with a reason
          </label>
        </div>
      </div>

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      <div className="mt-5 overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-700">
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Time</th>
              <th className="text-left px-3 py-2">Invoice</th>
              <th className="text-left px-3 py-2">Client</th>
              <th className="text-right px-3 py-2">Amount (BWP)</th>
              <th className="text-left px-3 py-2">Recorded By</th>
              <th className="text-left px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={7}>
                  {loading ? 'Loading…' : 'No results yet. Click Refresh to load.'}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.journal_id} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatTime(r.created_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.invoice_number}</td>
                  <td className="px-3 py-2">{r.client_name}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{r.amount}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.recorded_by || '—'}</td>
                  <td className="px-3 py-2">{r.reason || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-gray-500">
        Tip: set a date range then click Refresh, then Export PDF.
      </div>
    </div>
  );
}
